import { EMPTY, Observable, concat, of, race, from } from 'rxjs';
import { distinctUntilChanged, mergeMap, take, filter, catchError, map, takeWhile } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import Global from '../../../store/global-selectors';
import * as SelectedOrderActions from '../../../store/orders/selected-orders/orders.selected-orders.actions';
import * as OrderDetailsApi from '../../../store/orders/order-details/orders.details.api';
import { validateAndUpdateAddressObservable } from '../../../fulfillment-plans/fulfillment-plans.api';
import * as PrintActions from '../../../store/printing/print.actions';
import * as OrdersActions from '../../../store/orders/orders.actions';
import { BulkActionDataTypes } from '../../../services/bulk-update-type-data';
import * as CSWActions from '../../../store/csw/csw.actions';
import * as BulkUpdateActions from '../../../store/bulk/update/bulk.update.actions';
import * as BulkPrintActions from '../../../store/bulk/print/bulk.print.actions';
import { clearErrors as ClearSidebarErrorsAndWarnings } from '../../../store/sidebar-errors-and-warnings/sidebar-errors-and-warnings.actions';
import * as Actions from './orders.drawer.actions';
import { stockLevelRequestPreFlight } from '../../inventory/inventory.actions';
import { getActualSalesOrderIds, getWhitelistPages } from '../orders.helpers';
import { bulkRequestEntities } from '../../../store/entities/entities.actions';
import { bulkLoadOutOfDateEntities } from '../../../store/entities/services/entities.loader.helper';
import { BULK_UPDATE_DRAWER_BULK_UPDATE_JOB_ID } from './constants';
import { getBulkJobData$ } from '../../bulk/update/bulk.update.job.observables';
import { persistOrderConfiguration$ } from '../../csw/csw.observables';
import { VER, UNV, WRN, ERR } from '../../../enums/address-verification-status';
import { FormattedMessage } from 'react-intl';
import { messages } from './messages';
import { NotificationService, NOTIFICATION_DURATIONS, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import { createPusherMessageCache } from '../../pusher/message-cache';
import { BULK_APPLY_RATE_RECOMMENDATIONS } from '../../../services/pusher-service/pusher-contract-messages';
import { bulkApply } from '../../../fulfillment-planner/rate-recommendation/rate-recommendations.api';
import { bulkloadFulfillmentPlans } from '../../bulkload/bulkload.actions';
import { orderGridGetRatesRequest } from '../grid/orders.grid.header.actions';

const loadDrawerWithoutOrderDetails$ = (action$, getState, actions) => {
  return new Observable((observer) => {
    observer.next(
      race(
        action$.pipe(
          ofType(Actions.GET_DRAWER_ORDERS_SUCCESS),
          take(1),
          mergeMap(() => {
            const fulfillmentPlanIds = Global.selectSelectedOrdersIds(getState());
            if (fulfillmentPlanIds.length === 1) {
              const state = getState();
              const [fulfillmentPlanId] = fulfillmentPlanIds;
              const fulfillmentPlans = Global.selectEntitiesFulfillmentPlansByIds(state, {
                fulfillmentPlanIds,
              });
              return concat(
                persistOrderConfiguration$(action$, getState, {
                  fulfillmentPlanId,
                }),
                of(stockLevelRequestPreFlight({ fulfillmentPlans })),
              );
            }
            return EMPTY;
          }),
        ),
        action$.pipe(ofType(Actions.GET_DRAWER_ORDERS_FAILURE), take(1)),
      ),
    );

    actions.forEach((act) => observer.next(of(act)));
    observer.complete();
  }).pipe(mergeMap((next$) => next$));
};

const getDrawerActions = () => {
  const actions = [Actions.getDrawerOrders()];

  return actions;
};

// The order of the following two epics matters
export const selectOrdersEpic = (action$) =>
  action$.pipe(
    ofType(SelectedOrderActions.SET_SELECTED_ORDERS_PRE_CHECK),
    distinctUntilChanged((prev, curr) => {
      const currentIds = curr.selectedOrderIds;
      const prevIds = prev.selectedOrderIds;
      if (!currentIds.length) {
        return false;
      }
      return currentIds.join('') === prevIds?.join('');
    }),
    mergeMap((action) => of(SelectedOrderActions.setSelectedOrders(action.selectedOrderIds ?? [], action.entityId))),
  );

export const loadOrderDrawerEpic = (action$, { getState }, { history }) =>
  action$.pipe(
    ofType(SelectedOrderActions.SELECT_ALL_ORDERS, SelectedOrderActions.SET_SELECTED_ORDERS_PRE_CHECK),
    filter(() => {
      return getWhitelistPages(history);
    }),
    distinctUntilChanged((prev, curr) => {
      const currentIds = curr.selectedOrderIds;
      const prevIds = prev.selectedOrderIds;
      if (!currentIds || !currentIds.length) {
        return false;
      }
      return currentIds.join('') === prevIds?.join('');
    }),
    mergeMap((action) => {
      const actions = getDrawerActions(getState, action.selectedOrderIds);
      const state = getState();
      const areAllSelected = Global.selectSelectedOrdersAreAllOrdersSelected(state);
      if (!areAllSelected && action.selectedOrderIds.length === 0) {
        return of(Actions.clearDrawerOrders());
      }

      return concat(loadDrawerWithoutOrderDetails$(action$, getState, actions));
    }),
  );
// the order of the above two epics matters

export const clearDrawerErrorsAndWarnings = (action$) =>
  action$.pipe(
    ofType(Actions.CLEAR_DRAWER_ORDERS),
    mergeMap(() => {
      return of(ClearSidebarErrorsAndWarnings());
    }),
  );

const getActionsForSelectedDrawerOrders = (getState, fulfillmentPlanIds, history) => {
  const state = getState();
  if (fulfillmentPlanIds.length !== 1) {
    const actions = [of(Actions.getDrawerOrdersSuccess())];
    const bulkUpdateJob = Global.selectBulkUpdateBulkUpdateJob(state, {
      bulkUpdateJobId: BULK_UPDATE_DRAWER_BULK_UPDATE_JOB_ID,
    });

    const bulkUpdateTypeData = BulkActionDataTypes.getBulkUpdateTypeData('fulfillmentPlan');
    if (!bulkUpdateJob.id) {
      actions.push(
        of(
          BulkUpdateActions.startBulkUpdateJobFromDrawer(bulkUpdateTypeData, [], fulfillmentPlanIds, BULK_UPDATE_DRAWER_BULK_UPDATE_JOB_ID),
        ),
      );
    } else {
      actions.push(of(BulkUpdateActions.updateBulkUpdateJob(BULK_UPDATE_DRAWER_BULK_UPDATE_JOB_ID, { ids: fulfillmentPlanIds })));
      actions.push(getBulkJobData$(BULK_UPDATE_DRAWER_BULK_UPDATE_JOB_ID, undefined, getState, history));
    }
    return actions;
  }
  return [of(Actions.getDrawerOrdersSuccess())];
};

export const getDrawerOrdersEpic = (action$, { getState }, { history }) =>
  action$.pipe(
    ofType(Actions.GET_DRAWER_ORDERS_REQUEST),
    mergeMap(() => {
      const state = getState();
      const areAllSelected = Global.selectSelectedOrdersAreAllOrdersSelected(state);
      const fulfillmentPlanIds = Global.selectSelectedOrdersIds(state);
      const salesOrderIds = getActualSalesOrderIds(state, fulfillmentPlanIds);

      if (areAllSelected) {
        return concat(
          getBulkJobData$(BULK_UPDATE_DRAWER_BULK_UPDATE_JOB_ID, undefined, getState, history),
          of(Actions.getDrawerOrdersSuccess()),
        );
      }

      if (!salesOrderIds || salesOrderIds.length === 0) {
        return of(Actions.clearDrawerOrders());
      }

      return bulkLoadOutOfDateEntities(salesOrderIds, state).pipe(
        mergeMap(() => concat(...getActionsForSelectedDrawerOrders(getState, fulfillmentPlanIds, history))),
        catchError((error) => of(Actions.getDrawerOrdersFailure(error))),
      );
    }),
  );

export const validateAddress = (action$, { getState }) =>
  action$.pipe(
    ofType(Actions.VALIDATE_DRAWER_ADDRESS_REQUEST),
    mergeMap((action) => {
      const fulfillmentPlanIds = Global.selectSelectedOrdersIds(getState());
      return OrderDetailsApi.validateAddress(action.addresses).pipe(
        map(() => Actions.validateAddressSuccess(fulfillmentPlanIds)),
        catchError((err) => of(Actions.validateAddressFailure(err))),
      );
    }),
  );

const validateAndUpdateShipToAddress = (address, fulfillmentPlanIds) => {
  const { verificationStatus } = address;
  if (verificationStatus === ERR || verificationStatus === WRN) {
    return of(Actions.validateAddressFailure(address));
  }

  if (verificationStatus === VER || verificationStatus === UNV) {
    const request = {
      fulfillmentPlanIds,
      shipTo: address,
      validateAddress: true,
    };

    return validateAndUpdateAddressObservable(request).pipe(
      map(() => EMPTY),
      catchError((err) => of(Actions.validateAddressFailure(err))),
    );
  }
  return Actions.validateAddressFailure();
};

export const updateDrawerOrdersAfterAddressValidation = (action$, { getState }) =>
  action$.pipe(
    ofType(Actions.VALIDATE_DRAWER_ADDRESS_SUCCESS),
    mergeMap(({ fulfillmentPlanIds }) => {
      const state = getState();
      const fulfillmentPlans = Global.selectEntitiesFulfillmentPlansByIds(state, { fulfillmentPlanIds });
      fulfillmentPlans
        .map((fp) => ({
          shipTo: fp.shipTo,
          fulfillmentPlanId: fp.fulfillmentPlanId,
        }))
        .forEach((s) => {
          validateAndUpdateShipToAddress(s.shipTo, [s.fulfillmentPlanId]);
        });

      return of(OrdersActions.silentRequestGetOrders());
    }),
  );

export const refreshDrawer = (action$, { getState }) => {
  return action$.pipe(
    ofType(
      CSWActions.CSW_CREATE_LABEL_SUCCESS,
      PrintActions.MARK_LABEL_PRINTED_SUCCESS,
      PrintActions.MARK_PACKING_SLIP_PRINTED_SUCCESS,
      PrintActions.MARK_PICK_LIST_PRINTED_SUCCESS,
      OrdersActions.MARK_PAID_SUCCESS,
      OrdersActions.UPDATE_ORDER_SUCCESS,
      BulkPrintActions.BULK_PRINT_END,
    ),
    mergeMap(() => {
      const state = getState();
      const isSidebarVisible = Global.selectOrdersGridisSidebarVisible(state);
      const selectedOrderIds = getActualSalesOrderIds(state, Global.selectSelectedOrdersIds(state));
      if (isSidebarVisible) {
        return of(Actions.getDrawerOrders(), bulkRequestEntities({ salesOrderIds: selectedOrderIds }));
      }
      return EMPTY;
    }),
  );
};

const showBulkApplyRecommendationErrorNotification = () => {
  NotificationService.showNotification({
    title: <FormattedMessage {...messages.applySavingsFailed} />,
    notificationType: NOTIFICATION_TYPE_ERROR,
    duration: NOTIFICATION_DURATIONS.SHORT,
  });
};

export const bulkApplyRecommendations = (action$) =>
  action$.pipe(
    ofType(Actions.BULK_APPLY_RECOMMENDATIONS),
    mergeMap(({ bulkUpdateJobGuid, isRateShopperRecommendationsEnabled }) => {
      const { findMessage } = createPusherMessageCache([BULK_APPLY_RATE_RECOMMENDATIONS]);

      return from(bulkApply({ bulkUpdateJobGuid, isRateShopperRecommendationsEnabled })).pipe(
        mergeMap((bulkApplyRecommendationsResponse) => {
          const jobGuid = bulkApplyRecommendationsResponse.data.jobGuids[0];
          const messageChecker = (pusherMessage) => pusherMessage.messageGuid === jobGuid;
          const getIsJobComplete = (pusherMessage) => pusherMessage?.data?.final;

          const updatedFulfillmentPlanIds = [];
          const failedFulfillmentPlanIds = [];
          const amountSaved = {
            code: '',
            value: 0,
          };

          return findMessage(messageChecker).pipe(
            takeWhile((pusherMessage) => !getIsJobComplete(pusherMessage), true),
            mergeMap((pusherMessage) => {
              const isJobComplete = getIsJobComplete(pusherMessage);

              if (!isJobComplete) {
                const {
                  FulfillmentPlanId: fulfillmentPlanId,
                  IsSuccessful: isSuccessful,
                  TotalSavings: totalSavings,
                } = pusherMessage?.data?.result || {};

                amountSaved.value += totalSavings?.value || 0;
                amountSaved.code = totalSavings?.code || '';

                if (isSuccessful) {
                  updatedFulfillmentPlanIds.push(fulfillmentPlanId);
                } else {
                  failedFulfillmentPlanIds.push(fulfillmentPlanId);
                }

                return EMPTY;
              }

              if (failedFulfillmentPlanIds.length > 0) {
                showBulkApplyRecommendationErrorNotification();
              }

              return concat(
                of(
                  Actions.bulkApplyRecommendationsSuccess({
                    bulkUpdateJobGuid,
                    amountSaved,
                  }),
                ),
                of(bulkloadFulfillmentPlans(updatedFulfillmentPlanIds)),
                of(orderGridGetRatesRequest(updatedFulfillmentPlanIds)),
              );
            }),
          );
        }),
      );
    }),
    catchError(() => {
      showBulkApplyRecommendationErrorNotification();
    }),
  );
